/**
 * Edit Success Story Page - Admin only
 * @file src/web/features/achievements/pages/admin/edit/[id].tsx
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { useSuccessStory, useSuccessStories } from '../../../hooks/useSuccessStories';
import { SEO } from '../../../../../shared/components';
import {
  Save,
  Upload,
  X,
  User,
  Loader2,
  Award,
  Trash2,
  ArrowLeft
} from 'lucide-react';

const EditSuccessStoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const storyId = parseInt(id || '0');

  const { story, loading: loadingStory, error: loadError } = useSuccessStory(storyId);
  const { updateStory, uploadImage, deleteImage } = useSuccessStories(true);

  const [name, setName] = useState('');
  const [board, setBoard] = useState('CBSE Board');
  const [score, setScore] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [quote, setQuote] = useState('');
  const [currentSchool, setCurrentSchool] = useState('');
  const [order, setOrder] = useState(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when story is loaded
  useEffect(() => {
    if (story) {
      setName(story.name || '');
      setBoard(story.board || 'CBSE Board');
      setScore(story.score || '');
      setYear(story.year || new Date().getFullYear());
      setQuote(story.quote || '');
      setCurrentSchool(story.currentSchool || '');
      setOrder(story.order || 0);
      setExistingImageUrl(story.imageUrl || null);
    }
  }, [story]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeNewImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleDeleteExistingImage = async () => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    setIsDeletingImage(true);
    try {
      await deleteImage(storyId);
      setExistingImageUrl(null);
    } catch (err: any) {
      alert(err.message || 'Failed to delete image');
    } finally {
      setIsDeletingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !board.trim() || !score.trim()) {
      setError('Name, board, and score are required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update story
      await updateStory(storyId, {
        name: name.trim(),
        board: board.trim(),
        score: score.trim(),
        year,
        quote: quote.trim() || null,
        currentSchool: currentSchool.trim() || null,
        order,
      });

      // Upload new image if provided
      if (imageFile) {
        await uploadImage(storyId, imageFile);
      }

      navigate('/achievements/admin');
    } catch (err: any) {
      setError(err.message || 'Failed to update success story');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingStory) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          <span className="ml-2 text-gray-600">Loading story...</span>
        </div>
      </div>
    );
  }

  if (loadError || !story) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {loadError || 'Story not found'}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title={`Edit ${story.name}'s Story - Admin Panel`} />
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/achievements/admin')}
                className="p-2 bg-white/20 rounded-lg backdrop-blur-sm hover:bg-white/30 transition-colors"
              >
                <ArrowLeft className="h-6 w-6 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Edit Success Story
                </h1>
                <p className="mt-1 text-amber-100 text-sm">
                  Update {story.name}'s achievement details
                </p>
              </div>
            </div>
          </div>

          {/* Form Card */}
          <Card className="border-t-4 border-t-amber-500 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Award className="h-5 w-5 text-amber-600" />
                </div>
                Story Details
              </CardTitle>
              <CardDescription className="mt-1.5">
                Update the student's achievement information
              </CardDescription>
            </div>

            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                {/* Student Photo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Photo
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="New preview"
                          className="w-full h-full object-cover"
                        />
                      ) : existingImageUrl ? (
                        <img
                          src={existingImageUrl}
                          alt={story.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="image-upload"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {existingImageUrl ? 'Change Photo' : 'Upload Photo'}
                        </label>
                        {imageFile && (
                          <button
                            type="button"
                            onClick={removeNewImage}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Remove new image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {existingImageUrl && !imageFile && (
                          <button
                            type="button"
                            onClick={handleDeleteExistingImage}
                            disabled={isDeletingImage}
                            className="text-red-600 hover:text-red-700 p-2"
                            title="Delete current image"
                          >
                            {isDeletingImage ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Name and Board */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Name *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Lalenthika"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board/Curriculum *
                    </label>
                    <select
                      value={board}
                      onChange={(e) => setBoard(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    >
                      <option value="CBSE Board">CBSE Board</option>
                      <option value="IGCSE">IGCSE</option>
                      <option value="IB">IB Diploma</option>
                      <option value="State Board">State Board</option>
                      <option value="Entrance Exam">Entrance Exam</option>
                    </select>
                  </div>
                </div>

                {/* Score and Year */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Score/Grade *
                    </label>
                    <input
                      type="text"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      placeholder="e.g., 93.4% or A*"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="number"
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value))}
                      min={2020}
                      max={2030}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={order}
                      onChange={(e) => setOrder(parseInt(e.target.value))}
                      min={0}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Current School */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current School/Status
                  </label>
                  <input
                    type="text"
                    value={currentSchool}
                    onChange={(e) => setCurrentSchool(e.target.value)}
                    placeholder="e.g., Currently pursuing Science at DPS Modern Indian School, Qatar"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Quote/Testimonial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Quote/Testimonial
                  </label>
                  <textarea
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    placeholder="Student's testimonial about their experience at Tuition Point..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/achievements/admin')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EditSuccessStoryPage;
